import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  DODGE_DURATION_S,
  PLAYER_HEIGHT, PLAYER_RADIUS, PLAYER_SPEED,
  getClass,
} from "@mmo/shared";
import type { ModelEntry } from "./model-registry";
import { buildWeapon } from "./weapons";

type AnimName = "idle" | "run" | "jump" | "fire";

const PALETTE = [0xff5252, 0x4fc3f7, 0xffeb3b, 0x66bb6a, 0xab47bc, 0xff7043, 0x29b6f6, 0xec407a];
const DEFAULT_HAND = { x: 0.3, y: 1.0, z: -0.35 };

// Rotation pivot lives at this height (above the feet) so dodge rolls spin
// around the waist, not the feet.
const BODY_PIVOT_Y = 0.9;

// Module-level shared resources.
const TORSO_GEOM = new THREE.BoxGeometry(0.6, 0.7, 0.3);
const HEAD_GEOM  = new THREE.BoxGeometry(0.35, 0.35, 0.35);
const NOSE_GEOM  = new THREE.BoxGeometry(0.06, 0.06, 0.08);
const ARM_GEOM   = new THREE.BoxGeometry(0.15, 0.55, 0.15);
const LEG_GEOM   = new THREE.BoxGeometry(0.2, 0.7, 0.2);
const HEAD_MATERIAL = new THREE.MeshLambertMaterial({ color: 0xffd9b3 });
const skinMaterials = new Map<number, THREE.MeshLambertMaterial>();
function getSkinMaterial(color: number): THREE.MeshLambertMaterial {
  let m = skinMaterials.get(color);
  if (!m) { m = new THREE.MeshLambertMaterial({ color }); skinMaterials.set(color, m); }
  return m;
}

const _upAxis = new THREE.Vector3(0, 1, 0);
const _tmpAxisV = new THREE.Vector3();
const _tmpYawQ = new THREE.Quaternion();
const _tmpRollQ = new THREE.Quaternion();

const loader = new GLTFLoader();

export class Character {
  readonly group: THREE.Group;
  /** Inner wrapper that holds the rotation (yaw + roll). Lifted by BODY_PIVOT_Y. */
  private body: THREE.Group;
  private scene: THREE.Scene;
  private color: number;
  private entry: ModelEntry | undefined;
  private classId: string;

  private proc?: ProceduralRig;
  private mixer?: THREE.AnimationMixer;
  private clips: Partial<Record<AnimName, THREE.AnimationAction>> = {};
  private currentClip: AnimName | null = null;

  private weaponPrimary?: THREE.Mesh;
  private weaponMelee?: THREE.Mesh;

  private state: AnimName = "idle";
  private fireUntil = 0;
  private meleeUntil = 0;
  /** Remembered roll axis (x,z) captured when the dodge began, so the axis is stable mid-roll. */
  private rollAxisX = 1;
  private rollAxisZ = 0;
  private rollWasActive = false;
  /** Smoothed body yaw. Decouples from camera yaw while sprinting so the body
   *  faces the movement direction; snaps back to camera yaw otherwise. */
  private bodyYaw = 0;
  private bodyYawReady = false;
  private lastPoseTimeMs = 0;

  constructor(scene: THREE.Scene, id: string, classId: string, entry: ModelEntry | undefined) {
    this.scene = scene;
    this.entry = entry;
    this.classId = classId;
    this.group = new THREE.Group();
    this.body = new THREE.Group();
    this.body.position.y = BODY_PIVOT_Y;
    this.group.add(this.body);
    scene.add(this.group);
    const idx = parseInt(id, 10);
    this.color = entry?.color ?? PALETTE[(Number.isFinite(idx) ? idx : 0) % PALETTE.length];

    this.proc = buildProcedural(this.body, this.color);
    this.attachWeapons();

    if (entry?.url) this.loadGltf(entry).catch((err) => console.warn(`model load failed (${entry.url}):`, err));
  }

  setClassId(classId: string) {
    if (classId === this.classId) return;
    this.classId = classId;
    if (this.weaponPrimary) this.body.remove(this.weaponPrimary);
    if (this.weaponMelee) this.body.remove(this.weaponMelee);
    this.weaponPrimary = undefined;
    this.weaponMelee = undefined;
    this.attachWeapons();
  }

  private attachWeapons() {
    const cls = getClass(this.classId);
    const hand = (this.entry as any)?.handPosition ?? DEFAULT_HAND;

    this.weaponPrimary = buildWeapon(cls.primary.name);
    this.weaponPrimary.position.set(hand.x, hand.y - BODY_PIVOT_Y, hand.z);
    this.body.add(this.weaponPrimary);

    this.weaponMelee = buildWeapon(cls.melee.name);
    this.weaponMelee.position.set(-hand.x, hand.y - 0.1 - BODY_PIVOT_Y, 0);
    this.weaponMelee.rotation.z = -0.4;
    this.weaponMelee.visible = false;
    this.body.add(this.weaponMelee);
  }

  private async loadGltf(entry: ModelEntry) {
    const gltf = await loader.loadAsync(entry.url);
    const root = gltf.scene;
    if (entry.scale) root.scale.setScalar(entry.scale);
    root.position.y = (entry.yOffset ?? 0) - BODY_PIVOT_Y;
    if (entry.yawOffset) root.rotation.y = entry.yawOffset;

    if (this.proc) { this.body.remove(this.proc.root); this.proc = undefined; }
    this.body.add(root);

    if (gltf.animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(root);
      const byName = new Map<string, THREE.AnimationClip>();
      for (const c of gltf.animations) byName.set(c.name.toLowerCase(), c);
      const get = (key: AnimName): THREE.AnimationClip | undefined => {
        const o = entry.animationMap?.[key]?.toLowerCase();
        if (o && byName.has(o)) return byName.get(o);
        if (byName.has(key)) return byName.get(key);
        for (const [n, c] of byName) if (n.includes(key)) return c;
        return undefined;
      };
      for (const name of ["idle", "run", "jump", "fire"] as AnimName[]) {
        const clip = get(name);
        if (clip) this.clips[name] = this.mixer.clipAction(clip);
      }
      this.playClip(this.state);
    }
  }

  /**
   * Apply current authoritative state to the character.
   * dodgingTimer > 0 → play the roll animation around the move-perpendicular axis.
   */
  setPose(
    pos: { x: number; y: number; z: number },
    yaw: number,
    vx: number,
    vz: number,
    onGround: boolean,
    dodgingTimer: number,
  ) {
    const nowMs = performance.now();
    const dt = this.lastPoseTimeMs > 0
      ? Math.min(0.1, (nowMs - this.lastPoseTimeMs) / 1000)
      : 0.016;
    this.lastPoseTimeMs = nowMs;

    this.group.position.set(pos.x, pos.y, pos.z);

    const rolling = dodgingTimer > 0;
    const hSpeed = Math.hypot(vx, vz);

    // Body-yaw decoupling: while sprinting (moving faster than this player's
    // non-sprint cap), the body turns toward the movement direction. Otherwise
    // it smoothly returns to the camera yaw.
    const cls = getClass(this.classId);
    const baseMaxSpeed = PLAYER_SPEED * (cls.speedMultiplier > 0 ? cls.speedMultiplier : 1);
    const isSprinting = !rolling && hSpeed > baseMaxSpeed + 1.0;
    // velocity = (-sin(yaw)*spd, -cos(yaw)*spd) → so movement-yaw = atan2(-vx, -vz).
    const targetBodyYaw = isSprinting ? Math.atan2(-vx, -vz) : yaw;
    if (!this.bodyYawReady) {
      this.bodyYaw = targetBodyYaw;
      this.bodyYawReady = true;
    } else {
      let delta = targetBodyYaw - this.bodyYaw;
      while (delta >  Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      const turnRate = 14;        // ≈ 95% turn in ~0.2s
      this.bodyYaw += delta * Math.min(1, dt * turnRate);
    }

    if (rolling) {
      // First frame of the roll: capture and freeze the rotation axis so a velocity
      // wobble doesn't twist the axis mid-tumble.
      if (!this.rollWasActive) {
        if (hSpeed > 0.5) {
          // axis = up × dodgeDir; sign chosen so positive angle = forward tumble in motion.
          this.rollAxisX = -vz / hSpeed;
          this.rollAxisZ = vx / hSpeed;
        }
      }
      const p = 1 - Math.max(0, Math.min(1, dodgingTimer / DODGE_DURATION_S));
      const rollAngle = Math.PI * 2 * p;
      _tmpYawQ.setFromAxisAngle(_upAxis, this.bodyYaw);
      _tmpAxisV.set(this.rollAxisX, 0, this.rollAxisZ);
      _tmpRollQ.setFromAxisAngle(_tmpAxisV, rollAngle);
      this.body.quaternion.multiplyQuaternions(_tmpRollQ, _tmpYawQ);
    } else {
      this.body.quaternion.setFromAxisAngle(_upAxis, this.bodyYaw);
    }
    this.rollWasActive = rolling;

    const next: AnimName =
      nowMs < this.fireUntil ? "fire" :
      !onGround ? "jump" :
      hSpeed > 0.5 ? "run" : "idle";
    if (next !== this.state) { this.state = next; this.playClip(next); }

    if (this.proc) {
      const melee = nowMs < this.meleeUntil;
      const meleeT = melee ? (nowMs - (this.meleeUntil - 350)) / 350 : 0;
      animateProcedural(this.proc, next, hSpeed, nowMs, melee, meleeT, rolling);
      // Don't toggle weapon visibility during the roll — let the held weapon spin with you.
      if (!rolling) {
        if (this.weaponMelee) this.weaponMelee.visible = melee;
        if (this.weaponPrimary) this.weaponPrimary.visible = !melee;
      }
    }
  }

  triggerFire() {
    this.fireUntil = performance.now() + 150;
    if (this.clips.fire) {
      const a = this.clips.fire;
      a.reset();
      a.setLoop(THREE.LoopOnce, 1);
      a.clampWhenFinished = false;
      a.play();
    }
  }

  triggerMelee() {
    this.meleeUntil = performance.now() + 350;
    this.fireUntil = performance.now() + 150;
  }

  update(dt: number) {
    if (this.mixer) this.mixer.update(dt);
  }

  private playClip(name: AnimName) {
    const next = this.clips[name];
    if (!next) return;
    if (this.currentClip && this.clips[this.currentClip] && this.currentClip !== name) {
      this.clips[this.currentClip]!.fadeOut(0.15);
    }
    next.reset().fadeIn(0.15).play();
    this.currentClip = name;
  }

  dispose() {
    this.scene.remove(this.group);
  }
}

type ProceduralRig = {
  root: THREE.Group;
  torso: THREE.Mesh;
  leftArm: THREE.Object3D;
  rightArm: THREE.Object3D;
  leftLeg: THREE.Object3D;
  rightLeg: THREE.Object3D;
};

function buildProcedural(parent: THREE.Group, color: number): ProceduralRig {
  const root = new THREE.Group();
  // Compensate body's lift so rig parts can use their natural (feet=0) y coords.
  root.position.y = -BODY_PIVOT_Y;
  parent.add(root);
  const skinMat = getSkinMaterial(color);

  const torso = new THREE.Mesh(TORSO_GEOM, skinMat); torso.position.y = 1.05; root.add(torso);
  const head  = new THREE.Mesh(HEAD_GEOM, HEAD_MATERIAL); head.position.y = 1.6; root.add(head);
  const nose  = new THREE.Mesh(NOSE_GEOM, HEAD_MATERIAL); nose.position.set(0, 1.58, -0.18); root.add(nose);

  const mkArm = (x: number) => {
    const pivot = new THREE.Object3D();
    pivot.position.set(x, 1.35, 0);
    const arm = new THREE.Mesh(ARM_GEOM, skinMat);
    arm.position.y = -0.275;
    pivot.add(arm);
    root.add(pivot);
    return pivot;
  };
  const mkLeg = (x: number) => {
    const pivot = new THREE.Object3D();
    pivot.position.set(x, 0.7, 0);
    const leg = new THREE.Mesh(LEG_GEOM, skinMat);
    leg.position.y = -0.35;
    pivot.add(leg);
    root.add(pivot);
    return pivot;
  };

  return {
    root,
    torso,
    leftArm: mkArm(-0.375),
    rightArm: mkArm(0.375),
    leftLeg: mkLeg(-0.15),
    rightLeg: mkLeg(0.15),
  };
}

function animateProcedural(
  rig: ProceduralRig,
  state: AnimName,
  speed: number,
  nowMs: number,
  melee: boolean,
  meleeT: number,
  rolling: boolean,
) {
  const t = nowMs / 1000;

  if (rolling) {
    // Tucked-up roll pose: arms hugging chest, legs pulled in.
    rig.rightArm.rotation.x = -2.1;
    rig.rightArm.rotation.z = 0.4;
    rig.leftArm.rotation.x = -2.1;
    rig.leftArm.rotation.z = -0.4;
    rig.rightLeg.rotation.x = -1.4;
    rig.leftLeg.rotation.x = -1.4;
    rig.torso.position.y = 1.05;
    rig.torso.rotation.x = 0;
    return;
  }

  if (melee) {
    const angle = -1.6 + Math.sin(meleeT * Math.PI) * 2.0;
    rig.rightArm.rotation.x = angle;
    rig.rightArm.rotation.z = 0;
    rig.leftArm.rotation.x = -0.6;
    rig.leftLeg.rotation.x = 0;
    rig.rightLeg.rotation.x = 0;
    rig.torso.position.y = 1.05;
    rig.torso.rotation.x = 0;
    return;
  }

  // Default: right arm forward (holding the weapon).
  rig.rightArm.rotation.x = -1.25;
  rig.rightArm.rotation.z = 0.1;

  if (state === "run") {
    const freq = 6 * Math.max(0.4, speed / PLAYER_SPEED);
    const w = t * freq * 2 * Math.PI;
    const swing = Math.sin(w);
    // Leg + opposite-arm swing.
    rig.leftArm.rotation.x = swing * 0.9;
    rig.leftLeg.rotation.x = -swing * 0.9;
    rig.rightLeg.rotation.x = swing * 0.9;
    // Right arm/weapon bobs subtly with the run cycle.
    rig.rightArm.rotation.x = -1.25 + Math.abs(swing) * 0.08;
    // Torso dips on each footfall + slight forward lean.
    rig.torso.position.y = 1.05 - Math.abs(swing) * 0.04;
    rig.torso.rotation.x = 0.10;
  } else if (state === "jump") {
    rig.leftArm.rotation.x = -0.8;
    rig.leftLeg.rotation.x = -0.5;
    rig.rightLeg.rotation.x = -0.5;
    rig.torso.position.y = 1.05;
    rig.torso.rotation.x = 0.05;
  } else if (state === "fire") {
    // Recoil: right arm/shoulder kicks back, torso leans back briefly.
    rig.rightArm.rotation.x = -1.55;
    rig.leftArm.rotation.x = -1.05;
    rig.leftLeg.rotation.x = 0;
    rig.rightLeg.rotation.x = 0;
    rig.torso.position.y = 1.05;
    rig.torso.rotation.x = -0.06;
  } else {
    // Idle: gentle breathing.
    const breathe = Math.sin(t * 1.5);
    rig.leftArm.rotation.x = breathe * 0.04;
    rig.leftLeg.rotation.x = 0;
    rig.rightLeg.rotation.x = 0;
    rig.torso.position.y = 1.05 + breathe * 0.012;
    rig.torso.rotation.x = 0;
  }
}

export { PLAYER_HEIGHT, PLAYER_RADIUS };
